const textEncoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

const utf8Bytes = (value) => {
  const text = String(value ?? '');

  if (textEncoder) {
    return textEncoder.encode(text);
  }

  const bytes = [];
  for (let index = 0; index < text.length; index += 1) {
    const codePoint = text.codePointAt(index);

    if (codePoint > 0xffff) {
      index += 1;
    }

    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(
        0xc0 | (codePoint >> 6),
        0x80 | (codePoint & 0x3f)
      );
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    }
  }

  return new Uint8Array(bytes);
};

const crcTable = (() => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let crc = index;

    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 1) ? (0xedb88320 ^ (crc >>> 1)) : (crc >>> 1);
    }

    table[index] = crc >>> 0;
  }

  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (value) => {
  const buffer = new ArrayBuffer(2);
  new DataView(buffer).setUint16(0, value, true);
  return new Uint8Array(buffer);
};

const writeUint32 = (value) => {
  const buffer = new ArrayBuffer(4);
  new DataView(buffer).setUint32(0, value, true);
  return new Uint8Array(buffer);
};

const concatBytes = (chunks) => {
  const totalLength = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return merged;
};

const xmlEscape = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const sanitizeSheetName = (name, fallback = 'Sheet1') => {
  const sanitized = String(name ?? fallback)
    .replace(/[[\]:*?/\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return (sanitized || fallback).slice(0, 31);
};

const columnName = (index) => {
  let current = index + 1;
  let name = '';

  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }

  return name;
};

const estimateColumnWidth = (rows, columnIndex) => {
  let maxLength = 0;

  rows.forEach((row) => {
    const value = row?.[columnIndex];
    const length = String(value ?? '').length;
    if (length > maxLength) {
      maxLength = length;
    }
  });

  if (maxLength <= 0) {
    return 10;
  }

  return Math.min(Math.max(Math.ceil(maxLength * 1.15) + 2, 10), 45);
};

const buildColumnsXml = (rows) => {
  const maxColumns = rows.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);

  if (!maxColumns) {
    return '';
  }

  const columnsXml = [];

  for (let columnIndex = 0; columnIndex < maxColumns; columnIndex += 1) {
    const width = estimateColumnWidth(rows, columnIndex);
    const ref = columnIndex + 1;
    columnsXml.push(`<col min="${ref}" max="${ref}" width="${width}" customWidth="1"/>`);
  }

  return `<cols>${columnsXml.join('')}</cols>`;
};

const buildStyleSheetXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="4">
    <font>
      <sz val="11"/>
      <color rgb="FF1F2937"/>
      <name val="Calibri"/>
    </font>
    <font>
      <sz val="14"/>
      <b/>
      <color rgb="FF111827"/>
      <name val="Calibri"/>
    </font>
    <font>
      <sz val="10"/>
      <italic/>
      <color rgb="FF6B7280"/>
      <name val="Calibri"/>
    </font>
    <font>
      <sz val="11"/>
      <b/>
      <color rgb="FFFFFFFF"/>
      <name val="Calibri"/>
    </font>
  </fonts>
  <fills count="4">
    <fill>
      <patternFill patternType="none"/>
    </fill>
    <fill>
      <patternFill patternType="gray125"/>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FF374151"/>
      </patternFill>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFF3F4F6"/>
      </patternFill>
    </fill>
  </fills>
  <borders count="2">
    <border>
      <left/>
      <right/>
      <top/>
      <bottom/>
      <diagonal/>
    </border>
    <border>
      <left style="thin"><color rgb="FFE2E8F0"/></left>
      <right style="thin"><color rgb="FFE2E8F0"/></right>
      <top style="thin"><color rgb="FFE2E8F0"/></top>
      <bottom style="thin"><color rgb="FFE2E8F0"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>
  </cellStyleXfs>
  <cellXfs count="6">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1">
      <alignment vertical="center"/>
    </xf>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyAlignment="1">
      <alignment horizontal="left" vertical="center"/>
    </xf>
    <xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyAlignment="1">
      <alignment horizontal="left" vertical="center"/>
    </xf>
    <xf numFmtId="0" fontId="3" fillId="2" borderId="1" xfId="0" applyAlignment="1">
      <alignment horizontal="center" vertical="center" wrapText="1"/>
    </xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyAlignment="1">
      <alignment vertical="center"/>
    </xf>
    <xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyAlignment="1">
      <alignment vertical="center"/>
    </xf>
  </cellXfs>
  <cellStyles count="1">
    <cellStyle name="Normal" xfId="0" builtinId="0"/>
  </cellStyles>
</styleSheet>`;

const buildCellXml = (value, rowIndex, columnIndex, styleIndex = 0) => {
  const ref = `${columnName(columnIndex)}${rowIndex + 1}`;

  if (value === null || value === undefined || value === '') {
    return `<c r="${ref}" s="${styleIndex}" t="inlineStr"><is><t></t></is></c>`;
  }

  return `<c r="${ref}" s="${styleIndex}" t="inlineStr"><is><t>${xmlEscape(value)}</t></is></c>`;
};

const buildStyledRowXml = (values, rowIndex, styleIndex, height = null) => {
  const cellsXml = values
    .map((value, columnIndex) => buildCellXml(value, rowIndex, columnIndex, styleIndex))
    .join('');

  return `<row r="${rowIndex + 1}"${height ? ` ht="${height}" customHeight="1"` : ''}>${cellsXml}</row>`;
};

const buildSheetXml = (rows, options = {}) => {
  const {
    title = null,
    subtitle = null,
    autoFilter = true
  } = options;

  const dataRows = Array.isArray(rows) ? rows : [];
  const columnsXml = buildColumnsXml(dataRows);
  const maxColumns = dataRows.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0) || 1;
  const dataStartRow = 3;

  const sheetRows = [];

  if (title) {
    const titleRow = Array.from({ length: maxColumns }, (_, columnIndex) => (columnIndex === 0 ? title : ''));
    sheetRows.push(buildStyledRowXml(titleRow, 0, 1, 24));
  }

  if (subtitle) {
    const subtitleRow = Array.from({ length: maxColumns }, (_, columnIndex) => (columnIndex === 0 ? subtitle : ''));
    sheetRows.push(buildStyledRowXml(subtitleRow, 1, 2, 18));
  }

  dataRows.forEach((row, rowIndex) => {
    const sheetRowNumber = rowIndex + dataStartRow;
    const styleIndex = rowIndex === 0 ? 3 : (rowIndex % 2 === 1 ? 5 : 4);
    const cellsXml = row.map((value, columnIndex) => buildCellXml(value, sheetRowNumber - 1, columnIndex, styleIndex)).join('');
    sheetRows.push(`<row r="${sheetRowNumber}"${rowIndex === 0 ? ' ht="22" customHeight="1"' : ' ht="18" customHeight="1"'}>${cellsXml}</row>`);
  });

  const autoFilterRef = autoFilter && maxColumns > 0 && dataRows.length
    ? `A${dataStartRow}:${columnName(maxColumns - 1)}${dataStartRow + dataRows.length - 1}`
    : null;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheetViews>
    <sheetView workbookViewId="0">
      <pane ySplit="${dataStartRow}" topLeftCell="A${dataStartRow + 1}" activePane="bottomLeft" state="frozen"/>
      <selection pane="bottomLeft" activeCell="A${dataStartRow + 1}" sqref="A${dataStartRow + 1}"/>
    </sheetView>
  </sheetViews>
  ${columnsXml}
  <sheetData>${sheetRows.join('')}</sheetData>
  ${autoFilterRef ? `<autoFilter ref="${autoFilterRef}"/>` : ''}
</worksheet>`;
};

const buildWorkbookXml = (sheets) => {
  const sheetsXml = sheets.map((sheet, index) => {
    const sheetName = sanitizeSheetName(sheet.name, `Sheet${index + 1}`);
    return `<sheet name="${xmlEscape(sheetName)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`;
  }).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${sheetsXml}</sheets>
</workbook>`;
};

const buildWorkbookRelsXml = (sheets) => {
  const sheetRels = sheets.map((_, index) => (
    `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
  )).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${sheetRels}
  <Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
};

const buildRootRelsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`;

const buildContentTypesXml = (sheets) => {
  const overrides = sheets.map((_, index) => (
    `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  )).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
  ${overrides}
</Types>`;
};

const buildCorePropsXml = () => {
  const now = new Date().toISOString();

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties"
  xmlns:dc="http://purl.org/dc/elements/1.1/"
  xmlns:dcterms="http://purl.org/dc/terms/"
  xmlns:dcmitype="http://purl.org/dc/dcmitype/"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:creator>Horizon Group</dc:creator>
  <cp:lastModifiedBy>Horizon Group</cp:lastModifiedBy>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`;
};

const buildAppPropsXml = (sheets) => {
  const titles = sheets.map((sheet) => `<vt:lpstr>${xmlEscape(sanitizeSheetName(sheet.name))}</vt:lpstr>`).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"
  xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>Horizon Group</Application>
  <DocSecurity>0</DocSecurity>
  <ScaleCrop>false</ScaleCrop>
  <HeadingPairs>
    <vt:vector size="2" baseType="variant">
      <vt:variant><vt:lpstr>Worksheets</vt:lpstr></vt:variant>
      <vt:variant><vt:i4>${sheets.length}</vt:i4></vt:variant>
    </vt:vector>
  </HeadingPairs>
  <TitlesOfParts>
    <vt:vector size="${sheets.length}" baseType="lpstr">
      ${titles}
    </vt:vector>
  </TitlesOfParts>
</Properties>`;
};

const createZipBlob = (files) => {
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = utf8Bytes(file.path);
    const dataBytes = file.data instanceof Uint8Array ? file.data : utf8Bytes(file.data);
    const crc = crc32(dataBytes);
    const localHeader = concatBytes([
      writeUint32(0x04034b50),
      writeUint16(20),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint32(crc),
      writeUint32(dataBytes.length),
      writeUint32(dataBytes.length),
      writeUint16(nameBytes.length),
      writeUint16(0)
    ]);

    localParts.push(localHeader, nameBytes, dataBytes);

    const centralHeader = concatBytes([
      writeUint32(0x02014b50),
      writeUint16(20),
      writeUint16(20),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint32(crc),
      writeUint32(dataBytes.length),
      writeUint32(dataBytes.length),
      writeUint16(nameBytes.length),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint32(0),
      writeUint32(offset)
    ]);

    centralParts.push(centralHeader, nameBytes);
    offset += localHeader.length + nameBytes.length + dataBytes.length;
  });

  const centralDirectory = concatBytes(centralParts);
  const localData = concatBytes(localParts);
  const endRecord = concatBytes([
    writeUint32(0x06054b50),
    writeUint16(0),
    writeUint16(0),
    writeUint16(files.length),
    writeUint16(files.length),
    writeUint32(centralDirectory.length),
    writeUint32(localData.length),
    writeUint16(0)
  ]);

  return new Blob([localData, centralDirectory, endRecord], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
};

export const buildXlsxBlob = (sheets) => {
  const normalizedSheets = Array.isArray(sheets) ? sheets : [];
  const files = [
    { path: '[Content_Types].xml', data: buildContentTypesXml(normalizedSheets) },
    { path: '_rels/.rels', data: buildRootRelsXml() },
    { path: 'docProps/core.xml', data: buildCorePropsXml() },
    { path: 'docProps/app.xml', data: buildAppPropsXml(normalizedSheets) },
    { path: 'xl/workbook.xml', data: buildWorkbookXml(normalizedSheets) },
    { path: 'xl/_rels/workbook.xml.rels', data: buildWorkbookRelsXml(normalizedSheets) },
    { path: 'xl/styles.xml', data: buildStyleSheetXml() }
  ];

  normalizedSheets.forEach((sheet, index) => {
    files.push({
      path: `xl/worksheets/sheet${index + 1}.xml`,
      data: buildSheetXml(Array.isArray(sheet.rows) ? sheet.rows : [], {
        title: sheet.title,
        subtitle: sheet.subtitle,
        autoFilter: sheet.autoFilter ?? true
      })
    });
  });

  return createZipBlob(files);
};

export const downloadXlsxFile = ({ filename, sheets }) => {
  const blob = buildXlsxBlob(sheets);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
